import { writePath } from './contract.js';
import { formatOccurrence } from './schedule.js';

const pretty = (v) => JSON.stringify(v ?? {}, null, 2);
const clone = (v) => JSON.parse(JSON.stringify(v ?? {}));
const quoted = (options) => options.map((o) => `"${o}"`).join(', ');

const EMPTY_RESULT = {
    status: 'NOT_ACTIONABLE', answered: false, actions: [], changes: [], questions: [], note: null, ruleBreaks: [], artifacts: [],
    usedKnowledge: [],
};

// Server statuses: COMPLETED (with changes, or an answer), NEEDS_INPUT, NOT_ACTIONABLE, FAILED, MANUAL.
const CANT_RUN = new Set(['NOT_ACTIONABLE', 'FAILED']);

export const changesSomething = (view) => view.status === 'COMPLETED' && view.actions.length > 0;

export const resultView = (result, ctx) => {
    if (!result) return null;
    const breaks = (result.ruleBreaks || []).map((b) => {
        const rule = (ctx.rules || []).find((r) => b.startsWith(`${r.keyPath} `));
        return `Breaks the rule${rule ? ` "${rule.text}"` : ''} (${b}). Change the value or the rule.`;
    });
    const asked = (result.questions || []).map((q) => (q.options?.length ? `${q.text} Choose one: ${quoted(q.options)}` : q.text));
    const fileName = (id) => (ctx.configs || []).find((c) => c.id === id)?.name || id;
    const segmentName = (id) => (ctx.segments || []).find((s) => s.id === id)?.name || id;
    const actions = result.actions || [];
    const answered = result.status === 'COMPLETED' && actions.length === 0;
    return {
        status: result.status === 'COMPLETED' && breaks.length ? 'NEEDS_INPUT' : result.status,
        answered,
        actions: actions.map((a) => {
            if (a.type === 'AUDIENCE') return { type: a.type, segment: segmentName(a.segmentId) };
            if (a.type === 'WINDOW') return { type: a.type, hours: a.hours };
            return { type: a.type, file: fileName(a.fileId), fileId: a.fileId, key: a.key, op: String(a.op || 'SET').toLowerCase(), value: a.value };
        }),
        changes: result.changes || [],
        questions: CANT_RUN.has(result.status) ? [result.note].filter(Boolean) : [...asked, ...breaks],
        note: answered ? result.note : null,
        ruleBreaks: breaks,
        artifacts: (result.outputs || []).filter((o) => o.kind === 'artifact'),
        usedKnowledge: result.citedKnowledgeIds || [],
    };
};

const filesFor = (changes, ctx) => {
    const byFile = new Map();
    changes.forEach((c) => byFile.set(c.fileId, [...(byFile.get(c.fileId) || []), c]));
    return [...byFile.entries()].map(([fileId, list]) => {
        const base = (ctx.configs || []).find((c) => c.id === fileId);
        const after = clone(base?.content);
        list.forEach((c) => writePath(after, c.key, c.after));
        return { name: list[0].fileName || base?.name || fileId, beforeText: pretty(base?.content), afterText: pretty(after), changed: list.length };
    });
};

export const buildReport = (run, templateName, ctx) => {
    const tasks = (run.results || []).map((r) => {
        const result = resultView(r.result, ctx) || EMPTY_RESULT;
        return { id: r.itemId, title: r.title, result, files: filesFor(result.changes, ctx) };
    });
    const actions = tasks.flatMap((t) => t.result.actions);
    const audience = actions.filter((a) => a.type === 'AUDIENCE').map((a) => a.segment);
    const windows = actions.filter((a) => a.type === 'WINDOW');
    const questions = tasks.reduce((n, t) => n + (t.result.status === 'NEEDS_INPUT' ? t.result.questions.length : 0), 0);
    const unclear = tasks.filter((t) => t.result.status === 'NOT_ACTIONABLE').length;
    const failed = tasks.filter((t) => t.result.status === 'FAILED').length;
    const used = new Set(tasks.flatMap((t) => t.result.usedKnowledge));
    return {
        templateName,
        nextWhen: run.forOccurrenceStart ? formatOccurrence(new Date(run.forOccurrenceStart).getTime(), ctx.zone) : null,
        clean: questions === 0 && unclear === 0 && failed === 0 && tasks.some((t) => changesSomething(t.result)),
        questions,
        unclear,
        failed,
        tasks,
        rows: tasks.flatMap((t) => t.result.changes),
        audience: audience.length ? audience : ctx.audienceNames || [],
        windowHours: windows.length ? windows[windows.length - 1].hours : ctx.windowHours,
        personTasks: tasks.filter((t) => t.result.status === 'MANUAL').map((t) => t.title),
        ruleBreaks: tasks.flatMap((t) => t.result.ruleBreaks),
        usedKnowledge: (ctx.allSnippets || ctx.snippets || []).filter((s) => used.has(s.id)).map((s) => s.title),
    };
};

// The last finished test run's results. The server keeps them while a new attempt runs and after one fails,
// so they stay on screen until they are replaced.
export const taskResults = (run, tasks, ctx) => {
    if (!run?.ranAt) return {};
    const byItem = new Map((run.results || []).map((r) => [r.itemId, r]));
    return Object.fromEntries(tasks.map((t) => {
        const r = byItem.get(t.id);
        return [t.id, r && r.title === t.title && (r.actor || 'AI') === t.actor ? resultView(r.result, ctx) : null];
    }));
};
